import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import nivaasLogo from '../../assets/nivaas-logo.png';
import axios from 'axios';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [defaultsettings, setDefaultsettings] = useState([]);
    
  useEffect(() => {
    const fetchDefaultSettings = async () => {
      try {
        const settings_response = await axios.get('/api/saas/settings/default-settings');
        setDefaultsettings(settings_response.data);
      } catch (err) { 
        console.log('error');
      }
    };
    
    fetchDefaultSettings();
  }, []);

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No reset token provided. Please check your email link.');
        setValidating(false);
        return;
      }

      try {
        const response = await api.get(`/api/auth/verify-reset-token/${token}`);
        
        if (response.data.valid) {
          setTokenValid(true);
          setUserInfo(response.data.user);
        } else {
          setError(response.data.message || 'Invalid or expired reset token.');
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setError(
          err.response?.data?.message || 
          'Unable to validate reset token. The link may be expired or invalid.'
        );
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const { password, confirmPassword } = formData;
    
    // Validate inputs
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/auth/reset-password', {
        token,
        password,
        confirmPassword
      });

      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(
        err.response?.data?.message || 
        'Unable to reset password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Loading state while validating token
  if (validating) {
    return (
      <div className="container">
        <div className="row justify-content-center mt-5">
          <div className="col-md-6">
            <div className="card shadow">
              <div className="card-body p-5 text-center">
                <img 
                src={defaultsettings.logo_url ? defaultsettings.logo_url : nivaasLogo}
                alt="Nivaas Logo" className="mb-3" style={{ maxWidth: '150px', height: 'auto' }} />
                <div className="mb-3">
                  <div className="spinner-border" style={{ color: '#1F458E' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
                <p className="text-muted">Validating reset token...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="container">
        <div className="row justify-content-center mt-5">
          <div className="col-md-6">
            <div className="card shadow">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <img 
                  src={defaultsettings.logo_url ? defaultsettings.logo_url : nivaasLogo}
                  alt="Nivaas Logo" className="mb-3" style={{ maxWidth: '150px', height: 'auto' }} />
                  <div className="mb-3">
                    <i className="fas fa-check-circle" style={{ fontSize: '48px', color: '#28a745' }}></i>
                  </div>
                  <h2 className="mb-3" style={{ color: '#1F458E' }}>Password Reset Successfully!</h2>
                  <p className="text-muted mb-4">
                    Your password has been changed successfully. You can now log in with your new password.
                  </p>
                </div>

                <div className="alert alert-success">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>What&apos;s next?</strong>
                  <ul className="mb-0 mt-2">
                    <li>Use your new password to log in</li>
                    <li>Consider enabling two-factor authentication for added security</li>
                    <li>Keep your password secure and don&apos;t share it with anyone</li>
                  </ul>
                </div>

                <div className="d-grid">
                  <button 
                    className="btn py-2"
                    style={{ backgroundColor: '#D67836', color: 'white' }}
                    onClick={() => navigate('/login')}
                  >
                    <i className="fas fa-sign-in-alt me-2"></i>
                    Continue to Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token or error state
  if (!tokenValid) {
    return (
      <div className="container">
        <div className="row justify-content-center mt-5">
          <div className="col-md-6">
            <div className="card shadow">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <img 
                  src={defaultsettings.logo_url ? defaultsettings.logo_url : nivaasLogo}
                  alt="Nivaas Logo" className="mb-3" style={{ maxWidth: '150px', height: 'auto' }} />
                  <div className="mb-3">
                    <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                  </div>
                  <h2 className="mb-3" style={{ color: '#dc3545' }}>Invalid Reset Link</h2>
                  <p className="text-muted mb-4">
                    {error || 'This password reset link is invalid or has expired.'}
                  </p>
                </div>

                <div className="alert alert-warning">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>What you can do:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Request a new password reset email</li>
                    <li>Make sure you&apos;re using the latest reset link</li>
                    <li>Check that the entire link was copied correctly</li>
                  </ul>
                </div>

                <div className="d-grid gap-2">
                  <Link to="/forgot-password" className="btn py-2" style={{ backgroundColor: '#D67836', color: 'white' }}>
                    <i className="fas fa-envelope me-2"></i>
                    Request New Reset Email
                  </Link>
                  <Link to="/login" className="btn btn-outline-secondary">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="container">
      <div className="row justify-content-center mt-5">
        <div className="col-md-6">
          <div className="card shadow">
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <img 
                src={defaultsettings.logo_url ? defaultsettings.logo_url : nivaasLogo}
                alt="Nivaas Logo" className="mb-3" style={{ maxWidth: '150px', height: 'auto' }} />
                <h2 style={{ color: '#1F458E' }}>Set New Password</h2>
                {userInfo && (
                  <p className="text-muted">
                    Reset password for <strong>{userInfo.email}</strong>
                  </p>
                )}
              </div>
              
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="password" className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your new password"
                    required
                    disabled={loading}
                  />
                  <small className="text-muted">
                    Must be at least 6 characters with uppercase, lowercase, and number
                  </small>
                </div>

                <div className="mb-4">
                  <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your new password"
                    required
                    disabled={loading}
                  />
                  {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <small className="text-danger">Passwords do not match</small>
                  )}
                </div>
                
                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn py-2"
                    style={{ backgroundColor: '#D67836', color: 'white' }}
                    disabled={loading || (formData.password !== formData.confirmPassword)}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Resetting Password...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-lock me-2"></i>
                        Reset Password
                      </>
                    )}
                  </button>
                  
                  <Link to="/login" className="btn btn-outline-secondary">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Login
                  </Link>
                </div>
              </form>
            </div>
          </div>
          
          {/* Privacy Policy Footer */}
          <div className="text-center mt-3">
            <small className="text-muted">
              By using this service, you agree to our{' '}
              <Link to="/privacy-policy" className="text-decoration-none" style={{ fontSize: '11px', color: '#1F458E' }}>
                <i className="fas fa-shield-alt me-1"></i>
                Privacy Policy & Terms
              </Link>
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 